/**
 * Low-fit gating for the apply surfaces.
 *
 * The scorer already knows when a job is a long shot (`fitScoreBucket` → `low`,
 * typically zero required-skill overlap). Until 2026-09-03 nothing downstream
 * acted on it: the job page showed the "Autofill-ready" badge and a bare
 * "Apply now" on a Java role to a profile with no Java (audit §8.4). These
 * pure helpers decide what the UI does with a low bucket; the components
 * only render them.
 */
// Value import from the import-free leaf ONLY — this module is reached from
// "use client" components, and importing `./fit-score-breakdown` here pulled
// the D1 query layer into the client-component graph (+320 KiB gzip, deploy
// freeze 2026-09-03). `./score` is type-only and erased at compile time.
import { fitScoreBucket } from "./fit-bucket";
import type { FitScoreBreakdown } from "./score";

type Surface = "feed_card" | "job_detail";

/** Hide the AI-apply badge on the job page when the fit is `low`. Feed cards have no per-user fit; never hide there. */
export function shouldHideAiApplyBadge(
	surface: Surface,
	breakdown: FitScoreBreakdown | null | undefined,
): boolean {
	if (surface !== "job_detail" || !breakdown) return false;
	return fitScoreBucket(breakdown.score) === "low";
}

export interface LowFitNotice {
	headline: string;
	detail: string;
}

/**
 * One-line, factual notice beside the apply CTA on a `low` job. Names the
 * count ("0 of 5 required skills") because the scorer has it; never says
 * "don't apply" — the seeker decides, informed.
 */
export function lowFitNotice(
	locale: string,
	breakdown: FitScoreBreakdown | null | undefined,
): LowFitNotice | null {
	if (!breakdown || fitScoreBucket(breakdown.score) !== "low") return null;
	const matched = breakdown.skillOverlap.matched.length;
	const total = matched + breakdown.skillOverlap.missing.length;
	const isEn = locale === "en";
	if (total === 0) {
		return isEn
			? {
					headline: "Low match",
					detail:
						"Your profile matches little of what this job asks for. Applying is a long shot.",
				}
			: {
					headline: "Kecocokan rendah",
					detail:
						"Profilmu hanya sedikit cocok dengan yang diminta lowongan ini. Peluangnya kecil.",
				};
	}
	return isEn
		? {
				headline: `Matches ${matched} of ${total} required skills`,
				detail:
					matched === 0
						? "None of the required skills are on your profile. Applying is a long shot."
						: "Most required skills are missing from your profile. Applying is a long shot.",
			}
		: {
				headline: `Cocok ${matched} dari ${total} skill wajib`,
				detail:
					matched === 0
						? "Tidak ada skill wajib yang ada di profilmu. Peluangnya kecil."
						: "Sebagian besar skill wajib belum ada di profilmu. Peluangnya kecil.",
			};
}
